import { pick } from "@nosto/search-js/utils"
import DynamicCardProduct from "@/components/Product/DynamicCardProduct"
import { useDecoratedSearchResults, useNostoAppState } from "@nosto/search-js/preact/hooks"
import style from "./Products.module.css"
import { cl } from "@nosto/search-js/utils"
import { defaultSize, hitDecorators, skeletonLoading, styleFamilyCatalogMaxRawTotal } from "@/config"
import { dedupeByStyleFamily } from "@/utils/dedupeByStyleFamily"
import { buildSkeletonHits } from "@/components/SearchQueryHandler/skeletonContent"
import { useStyleFamilyCatalog } from "@/contexts/StyleFamilyCatalogContext"
import { useMemo } from "preact/hooks"
import type { Product } from "@/types"
import { isRenderableProductHit } from "@/utils/isRenderableProductHit"
import { readInGridConfig, type InGridConfig } from "@/utils/ingridConfig"
import { computeInGridSlotCount, pageSliceWithInGrid, totalPagesWithInGrid } from "@/utils/paginationWithInGrid"
import InGridTile from "@/components/InGridTile/InGridTile"
import { useDynamicCardGridReady } from "@/hooks/useDynamicCardGridReady"

type GridItem = { kind: "product"; product: Product; index: number } | { kind: "tile"; config: InGridConfig }

/**
 * Interleave in-grid content card tiles into the product list based on each
 * tile's `position` (1-based index in the product sequence where the tile
 * appears *before* that product). Tiles that span multiple columns are placed
 * so they start at column 0 of the grid to avoid layout gaps.
 */
function interleaveItems(products: Product[], tiles: InGridConfig[]): GridItem[] {
  if (tiles.length === 0) {
    return products.map((product, index) => ({ kind: "product", product, index }))
  }

  const sorted = [...tiles].sort((a, b) => a.position - b.position)
  const result: GridItem[] = []
  let tileIdx = 0
  let productIdx = 0

  for (let visibleProduct = 1; productIdx < products.length; visibleProduct++) {
    while (tileIdx < sorted.length && sorted[tileIdx].position <= visibleProduct) {
      result.push({ kind: "tile", config: sorted[tileIdx] })
      tileIdx++
    }
    result.push({ kind: "product", product: products[productIdx], index: productIdx })
    productIdx++
  }

  while (tileIdx < sorted.length) {
    result.push({ kind: "tile", config: sorted[tileIdx] })
    tileIdx++
  }

  return result
}

/**
 * PLP / category / search-result product grid.
 *
 * Sibling color products with the same `styleFamilyKey` are collapsed by
 * `dedupeByStyleFamily`. When raw `products.total` is within
 * `styleFamilyCatalogMaxRawTotal`, the app loads all hits, dedupes once, and
 * paginates client-side so counts and pages match visible products.
 */
export default function Products() {
  const { loading, initialized } = useNostoAppState(state => pick(state, "loading", "initialized"))
  const pageSize = useNostoAppState(state => state.query?.products?.size ?? defaultSize)
  const { products } = useDecoratedSearchResults<typeof hitDecorators>()
  const catalog = useStyleFamilyCatalog()

  const effectiveInitialized = initialized ?? true

  const inGridConfigs = useMemo(() => readInGridConfig(), [])
  const inGridSlots = useMemo(() => computeInGridSlotCount(inGridConfigs), [inGridConfigs])

  // Page index for the raw fallback path (catalog uses its own `urlPage`).
  const rawPage = useNostoAppState(state => {
    const from = state.query?.products?.from ?? 0
    const size = state.query?.products?.size ?? defaultSize
    return Math.floor(from / size) + 1
  })

  const realHits = useMemo(() => {
    if (catalog?.status === "ready" && catalog.dedupedHits && catalog.dedupedHits.length > 0) {
      const totalDeduped = catalog.dedupedHits.length
      const maxPage = Math.max(1, totalPagesWithInGrid(totalDeduped, pageSize, inGridSlots))
      const safePage = Math.min(Math.max(1, catalog.urlPage), maxPage)
      const { start, end } = pageSliceWithInGrid(safePage, pageSize, inGridSlots)
      const safeStart = Math.min(start, totalDeduped)
      const safeEnd = Math.min(end, totalDeduped)
      return catalog.dedupedHits.slice(safeStart, safeEnd)
    }
    const rawHits = products?.hits ?? []
    const renderableHits = rawHits.filter(isRenderableProductHit)
    const hits = dedupeByStyleFamily(renderableHits)
    // Raw fallback: only the first page reserves grid cells for in-grid tiles.
    // Trim the trailing `inGridSlots` products on page 1 so the visible row
    // count stays uniform. Other pages keep all returned hits.
    if (rawPage === 1 && inGridSlots > 0) {
      return hits.slice(0, Math.max(0, hits.length - inGridSlots))
    }
    return hits
  }, [catalog?.status, catalog?.dedupedHits, catalog?.urlPage, pageSize, products?.hits, inGridSlots, rawPage])

  const catalogPriming =
    catalog != null &&
    catalog.rawTotal > 0 &&
    catalog.rawTotal <= styleFamilyCatalogMaxRawTotal &&
    (catalog.status === "idle" || catalog.status === "loading")
  const logicalLoading = skeletonLoading && (!effectiveInitialized || loading || catalogPriming)
  const realGridSignature = useMemo(
    () => realHits.map((hit, index) => hit.productId ?? hit.handle ?? hit.url ?? index).join("|"),
    [realHits]
  )
  const { gridRef, ready: realGridReady } = useDynamicCardGridReady({
    enabled: skeletonLoading && !logicalLoading && realHits.length > 0,
    expectedCount: realHits.length,
    signature: realGridSignature
  })
  const waitingForCards = skeletonLoading && !logicalLoading && realHits.length > 0 && !realGridReady
  const showSkeleton = logicalLoading || waitingForCards
  const skeletonHits = useMemo(() => buildSkeletonHits(defaultSize), [])

  // In-grid blocks render on the first page only.
  const isFirstPage = (catalog?.status === "ready" ? catalog.urlPage : rawPage) === 1
  const visibleInGridConfigs = isFirstPage ? inGridConfigs : []

  const gridItems = useMemo(
    () => (logicalLoading ? [] : interleaveItems(realHits, visibleInGridConfigs)),
    [logicalLoading, realHits, visibleInGridConfigs]
  )

  return (
    <div className={style.shell} data-nosto-products-state={showSkeleton ? "loading" : "ready"}>
      {showSkeleton && (
        <div className={cl(style.container, style.skeletonGrid)} data-nosto-products-skeleton>
          {skeletonHits.map((hit: Product, index: number) => (
            <DynamicCardProduct product={hit} key={hit.productId || index} />
          ))}
        </div>
      )}
      {!logicalLoading && (
        <div
          ref={gridRef}
          className={cl(style.container, waitingForCards && style.hiddenGrid)}
          data-nosto-products-grid
          aria-hidden={waitingForCards ? "true" : "false"}
        >
          {gridItems.map((item, i) =>
            item.kind === "product" ? (
              <DynamicCardProduct product={item.product} productIndex={item.index} key={item.product.productId || i} />
            ) : (
              <InGridTile config={item.config} key={`ingrid-${item.config.type}-${item.config.position}`} />
            )
          )}
        </div>
      )}
    </div>
  )
}
